<template>
  <BaseBody>
    <BaseTopBar>
      <BaseTopBarContent className="left">
        {{ $t('sidebar.billingserviceused') }}
      </BaseTopBarContent>
      <BaseTopBarContent className="right">
        <div class="action">
          <BaseDropdown
            v-model="date"
            :classNames="['form-control-sm']"
            :options="dateOptions"
            @change="handleFilterChange"
          />
        </div>
        <div
          class="action mr-3"
          style="display: flex;align-items: center;height: 31px;"
        >
          <span
            v-for="(text, index) in textDiscounts"
            :key="index"
            class="badge badge-danger"
          >
            {{ text }}
          </span>
        </div>
      </BaseTopBarContent>
    </BaseTopBar>
    <BaseContent :loading="loading">
      <div
        v-if="showPricingAlert"
        class="row"
        style="margin-bottom: 80px;">
        <div class="col">
          <div
            class="alert alert-info text-center"
            role="alert"
            style="margin-bottom: -50px;">
            {{ $t('message.pricingalertstartdate') }}
          </div>
        </div>
      </div>
      <ServiceUsedSummary
        :data="data"
        :apps="apps"
        @onOpenUsageModal="handleOpenUsageModal"
      />
      <BillingInvoiceUsage
        v-if="tabSelected"
        v-model="tabSelected"
        :dateStart="dateStart"
        :dateEnd="dateEnd"
        :records="creditRecords"
        :files="creditFiles"
        :lineMessages="creditLineMessages"
        :emails="creditEmails"
        :qrcodes="creditQRCodes"
        :branches="creditBranches"
        :warehouses="creditWarehouses"
        :users="creditUsers"
        :apps="creditApps"
      />

      <BaseAppModuleListModule
        :items="usageInfoApps"
        :show="usageModal.show"
        :title="usageModal.title"
        @onClose="handleCloseUsageModal"
      />
    </BaseContent>
  </BaseBody>
</template>

<script>
import moment from 'moment'
import { mapState, mapGetters, mapActions } from 'vuex'
import { BILLING_SERVICE_USAGE, HAS_FEE_APP_MODULES } from '../../../../helpers/enum'
import sidebarModule from './../../sidebarModule'

export const UsageInfoType = {
  Record: 'record',
  File: 'file',
  MessageLine: 'messageLine',
  Email: 'email',
  QRCode: 'qrcode',
}

export default {
  data () {
    return {
      UsageInfoType,
      loading: false,
      date: moment().format('YYYY-MM'),
      usageModal: {
        show: false,
        title: null,
      },
      usageInfoType: UsageInfoType.Record,
      tabSelected: null,
      defaultFormat: 'YYYY-MM-DDTHH:mm:ss.SSS[Z]',
    }
  },
  computed: {
    ...mapState('billing', {
      data: 'monthlyUsed',
      creditRecords: 'monthlyUsedCreditRecordList',
      creditFiles: 'monthlyUsedCreditFileList',
      creditLineMessages: 'monthlyUsedCreditLineMessageList',
      creditEmails: 'monthlyUsedCreditEmailList',
      creditQRCodes: 'monthlyUsedCreditQRCodeList',
      creditBranches: 'monthlyUsedCreditBranchList',
      creditWarehouses: 'monthlyUsedCreditWarehouseList',
      creditUsers: 'monthlyUsedCreditUserList',
      creditApps: 'monthlyUsedCreditAppList',
    }),
    ...mapGetters([
      'getApps',
      'getAppInfo',
      'getCurrency',
      'getLang',
    ]),
    ...mapGetters({
      me: 'getMe',
      getApps: 'getApps',
      shopDiscounts: 'getShopDiscounts',
    }),
    apps () {
      return this.getApps.map((app) => {
        return {
          ...app,
          title: this.$t(`apps.billing.apps.appid${app.app_id}`),
          modules: app.modules.map((module) => {
            return {
              ...module,
              title: this.$t(`apps.billing.apps.appid${app.app_id}-moduleid${module.module_id}`),
            }
          }),
        }
      }).reduce((acc, cur) => {
        cur.modules.forEach(module => {
          const amountTotal = (() => {
            const result = this.data.apps.find((app) => (
              app.appId === cur.app_id &&
                  app.moduleId === module.module_id
            ))
            if (result != null) {
              return result.amountTotal
            }
            return null
          })()
          const hasFee = amountTotal != null
          if (hasFee) {
            acc.push({
              app: {
                id: cur.app_id,
                title: cur.title,
              },
              module: {
                id: module.module_id,
                title: module.title,
              },
              freeUsedTotal: 0,
              usedTotal: 1,
              chargeUsedTotal: 1,
              amountTotal: amountTotal,
            })
          }
        })
        return acc
      }, [])
    },
    dateOptions () {
      const options = []
      let showYear = false
      let currentYear = null
      const lastMonthsBack = 12
      for (let i = 0; i < lastMonthsBack; i++) {
        const date = moment().subtract(i, 'month')
        const monthIndex = date.format('M') - 1
        if (currentYear && currentYear !== date.format('YYYY')) {
          showYear = true
        }
        currentYear = date.format('YYYY')
        options.push({
          text: `${this.getLocalMonth(monthIndex, this.getLang)}` + (showYear ? ` ${date.format('YYYY')}` : ''),
          value: date.format('YYYY-MM'),
        })
      }
      return options
    },
    usageInfoApps () {
      return Object.keys(this.data[this.usageInfoType].count).reduce((acc, cur) => {
        const { appId, moduleId } = (() => {
          const obj = {
            appId: null,
            moduleId: null,
          }
          try {
            const re = /app_([0-9]{0,2})_module_([0-9]{0,2})$/
            const arr = cur.match(re)
            obj.appId = Number(arr[1])
            obj.moduleId = Number(arr[2])
            return obj
          } catch (e) {
            return obj
          }
        })()
        const count = this.data[this.usageInfoType].count[cur]
        const text = this.$t(`moduletransaction.${this.getAppInfo(appId).translate}.module${moduleId}`, { number: this.numberel(count, '0,0.[00]') })
        const findIndex = acc.findIndex((item) => item.app_id === appId)
        if (findIndex === -1) {
          acc.push({
            app_id: appId,
            modules: [text],
          })
        } else {
          acc[findIndex].modules.push(text)
        }
        return acc
      }, [])
    },
    dateStart () {
      return this.moment(this.date).startOf('month').format(this.defaultFormat)
    },
    dateEnd () {
      const isCurrentMonth = (() => {
        const currentMonth = this.moment().format('YYYY-M')
        const selectedMonth = this.moment(this.date).format('YYYY-M')
        return currentMonth === selectedMonth
      })()
      if (isCurrentMonth) {
        return this.moment().utc().set({ minute: 0, second: 0, millisecond: 0 }).format(this.defaultFormat)
      }
      return this.moment(this.date).endOf('month').set({ minute: 0, second: 0, millisecond: 0 }).format(this.defaultFormat)
    },
    showPricingAlert () {
      const currentTime = new Date().getTime()
      const startTime = new Date('2023-02-01T00:00:00.000Z').getTime()
      const timePassed = currentTime > startTime
      return this.me.type === 'owner' && !timePassed
    },
    textDiscounts() {
      return this.shopDiscounts.map((discount) => {
        const expiredAt = this.moment(discount.expired_at)
        const monthsLeft = expiredAt.diff(this.moment(), 'months', true)
        return this.$t('message.getdiscount', {
          discount: `${discount.value_amount}${discount.value_type === 'percentage' ? '%' : ''}`,
          expire: Math.ceil(monthsLeft),
        })
      })
    },
  },
  beforeMount () {
    this.initPage({
      section: 'app',
      name: this.$t('app.billing.title'),
      current: {
        app_name: 'billing',
        app_id: null,
      },
    })
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 0,
    })
  },
  async mounted () {
    this.fetchData()
  },
  destroyed () {
    this.leavePage(this.$Progress, [])
  },
  methods: {
    ...mapActions('billing', {
      getMonthlyUsed: 'getMonthlyUsed',
      getMonthlyUsedCreditRecordList: 'getMonthlyUsedCreditRecordList',
      getMonthlyUsedCreditFileList: 'getMonthlyUsedCreditFileList',
      getMonthlyUsedCreditLineMessageList: 'getMonthlyUsedCreditLineMessageList',
      getMonthlyUsedCreditEmailList: 'getMonthlyUsedCreditEmailList',
      getMonthlyUsedCreditQRCodeList: 'getMonthlyUsedCreditQRCodeList',
      getMonthlyUsedCreditBranchList: 'getMonthlyUsedCreditBranchList',
      getMonthlyUsedCreditWarehouseList: 'getMonthlyUsedCreditWarehouseList',
      getMonthlyUsedCreditUserList: 'getMonthlyUsedCreditUserList',
      getMonthlyUsedCreditAppList: 'getMonthlyUsedCreditAppList',
    }),
    ...mapActions({
      getShopDiscounts: 'getShopDiscounts',
    }),
    async fetchData () {
      this.loading = true
      const getApps = HAS_FEE_APP_MODULES.map((app) => (
        this.getMonthlyUsedCreditAppList({ date: this.date,
          params: {
            app_id: app.app_id,
            module_id: app.module_id,
          } })
      ))
      await Promise.all([
        this.getMonthlyUsed(this.date),
        this.getMonthlyUsedCreditRecordList(this.date),
        this.getMonthlyUsedCreditFileList(this.date),
        this.getMonthlyUsedCreditLineMessageList(this.date),
        this.getMonthlyUsedCreditEmailList(this.date),
        this.getMonthlyUsedCreditQRCodeList(this.date),
        this.getMonthlyUsedCreditBranchList(this.date),
        this.getMonthlyUsedCreditWarehouseList(this.date),
        this.getMonthlyUsedCreditUserList(this.date),
        ...getApps,
        this.getShopDiscounts(),
      ])
      this.loading = false
      this.showFinish(this.$Progress)
      this.setState()
    },
    setState () {
      if (this.creditBranches.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.BRANCH
      } else if (this.creditWarehouses.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.WAREHOUSE
      } else if (this.creditUsers.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.USER
      } else if (this.creditRecords.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.RECORD
      } else if (this.creditFiles.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.FILE
      } else if (this.creditLineMessages.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.MESSAGE_LINE
      } else if (this.creditEmails.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.EMAIL
      } else if (this.creditQRCodes.length) {
        this.tabSelected = BILLING_SERVICE_USAGE.QRCODE
      } else {
        this.tabSelected = null
      }

      if (this.tabSelected == null) {
        const hasDataInKey = Object.keys(this.creditApps).reduce((acc, cur) => {
          const key = cur
          if (this.creditApps[key].length && !acc) {
            acc = key
          }
          return acc
        }, null)
        this.tabSelected = hasDataInKey
      }
    },
    handleFilterChange () {
      this.fetchData()
    },
    handleOpenUsageModal (type) {
      const mapUsageModalTitle = {
        [UsageInfoType.Record]: this.$t('apps.billing.message.records', { number: this.data.record.countAllTotal }),
        [UsageInfoType.File]: this.$t('apps.billing.message.files', { number: this.data.file.countAllTotal }),
        [UsageInfoType.MessageLine]: this.$t('apps.billing.message.linemessages', { number: this.data.messageLine.countAllTotal }),
        [UsageInfoType.Email]: this.$t('apps.billing.message.emails', { number: this.data.email.countAllTotal }),
        [UsageInfoType.QRCode]: this.$t('apps.billing.message.qrcodes', { number: this.data.qrcode.countAllTotal }),
      }
      this.usageInfoType = type
      this.usageModal.show = true
      this.usageModal.title = mapUsageModalTitle[type]
    },
    handleCloseUsageModal () {
      this.usageModal.show = false
    },
  },
}
</script>

<style scoped>
.custom-table /deep/ .table tr:hover {
  background: none;
}

.summary-container {
  display: flex;
  margin-top: 15px;
}

.summary-container .text {
  flex: 1;
  text-align: right;
}

.summary-container .value {
  width: 180px;
  padding-right: 11px;
  text-align: right;
}

.link {
  text-decoration: underline;
}

.link:hover {
  color: var(--link-color);
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/apps/billing/serviceused/Index.vue